import { useEffect, useContext, useState, useCallback } from 'react';
import { UserContext } from '../components/UserContext';

/**
 * Custom hook to manage session timeout and auto-logout
 * Warns user 5 minutes before session expires (JWT expires in 1 hour = 3600 seconds)
 *
 * @param {number} warningMinutes - Minutes before expiration to show warning (default: 5)
 * @returns {object} - { showWarning, timeRemaining, extendSession, logout }
 */
export const useSessionTimeout = (warningMinutes = 5) => {
  const { logout, token } = useContext(UserContext);
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);

  // Session duration: 1 hour (3600 seconds) - matches JWT expiration in server
  const SESSION_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  const WARNING_TIME = warningMinutes * 60 * 1000; // Warning threshold in milliseconds
  const CHECK_INTERVAL = 1000; // Check every second

  // Initialize session start time when token exists
  useEffect(() => {
    if (token && !sessionStartTime) {
      // Set session start to current time when user logs in
      setSessionStartTime(Date.now());
    }
  }, [token, sessionStartTime]);

  // Handle session timeout and warnings
  useEffect(() => {
    if (!token || !sessionStartTime) return;

    const checkSessionTimeout = setInterval(() => {
      const now = Date.now();
      const elapsedTime = now - sessionStartTime;
      const timeUntilExpiry = SESSION_DURATION - elapsedTime;

      // Session has expired
      if (timeUntilExpiry <= 0) {
        clearInterval(checkSessionTimeout);
        setShowWarning(false);
        logout();
        return;
      }

      // Show warning when time remaining falls below warning threshold
      if (timeUntilExpiry <= WARNING_TIME) {
        setShowWarning(true);
        setTimeRemaining(Math.floor(timeUntilExpiry / 1000)); // Remaining seconds
      } else if (showWarning) {
        // Hide warning if user already saw it and we're back above threshold
        setShowWarning(false);
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(checkSessionTimeout);
  }, [token, sessionStartTime, showWarning, logout, SESSION_DURATION, WARNING_TIME]);

  // Extend session by getting a new token
  const extendSession = useCallback(async () => {
    try {
      setShowWarning(false);

      // Call refresh-token endpoint to get a new token
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          logout();
          return;
        }
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();

      // Update token in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Reset session start time to extend session
      setSessionStartTime(Date.now());

      console.log('✅ Session extended successfully');
    } catch (error) {
      console.error('Failed to extend session:', error);
      logout();
    }
  }, [token, logout]);

  // Logout immediately
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
  }, [logout]);

  return {
    showWarning,
    timeRemaining, // in seconds
    extendSession,
    logout: handleLogout
  };
};
